import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
} from 'recharts';
import { Paper, Alert } from '@mantine/core';
import { useChartHeight } from '@/hooks/useChartHeight';
import { useIsMobile } from '@/hooks/useIsMobile';
import { calculateNiceAxisTicks } from '@/utils/formatters';
import { useTimeAxis } from '@/hooks/useTimeAxis';
import { useMemo, useRef } from 'react';
import { ChartSkeleton } from '@/components/charts/ChartSkeleton';
import { ChartHeader } from '@/components/charts/ChartHeader';
import { CHART_GRID_STROKE, CHART_AXIS_TICK, CHART_LEGEND_STYLE } from '@/utils/chartStyles';
import { CHART_CROSSHAIR_COLOR } from '@/utils/chartColors';
import { ChartTooltipContent } from '@/components/charts/ChartTooltipContent';
import { ANALYSIS_SERIES_COLORS } from '@/utils/chartColors';
import type { PowerDataPoint, PowerChannel } from '@/api/power';

interface VoltageChartProps {
  data: PowerDataPoint[];
  voltageChannels: PowerChannel[];
  isLoading?: boolean;
  error?: Error | null;
}

export function VoltageChart({
  data,
  voltageChannels,
  isLoading,
  error,
}: VoltageChartProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartHeight = useChartHeight();
  const isMobile = useIsMobile();

  // Convert string timestamps to numbers for the numeric X-axis
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return data.map(point => ({
      ...point,
      timestamp: new Date(point.timestamp).getTime(),
    }));
  }, [data]);

  // Derive date range from data for time axis
  const { startDate, endDate } = useMemo(() => {
    if (chartData.length === 0) {
      const now = new Date();
      return { startDate: now, endDate: now };
    }
    return {
      startDate: new Date(chartData[0]!.timestamp),
      endDate: new Date(chartData[chartData.length - 1]!.timestamp),
    };
  }, [chartData]);

  const timeAxis = useTimeAxis(startDate, endDate);

  // Calculate Y-axis from voltage data
  const yAxisConfig = useMemo(() => {
    if (!data || data.length === 0) {
      return calculateNiceAxisTicks(100, 260, 5, false);
    }

    const values = data
      .flatMap(d =>
        voltageChannels.map(ch => {
          const v = d[ch.did];
          return typeof v === 'number' ? v : null;
        })
      )
      .filter((v): v is number => v != null && !isNaN(v));

    if (values.length === 0) {
      return calculateNiceAxisTicks(100, 260, 5, false);
    }

    // Reduce rather than spread: Math.min(...values) overflows the call stack
    // on long date ranges.
    const min = values.reduce((a, b) => (b < a ? b : a));
    const max = values.reduce((a, b) => (b > a ? b : a));
    return calculateNiceAxisTicks(min, max, 5, false);
  }, [data, voltageChannels]);

  // Early returns AFTER all hooks
  if (isLoading) {
    return <ChartSkeleton title="Voltage" />;
  }

  if (error) {
    return (
      <Alert color="red" title="Error loading voltage data">
        {error.message}
      </Alert>
    );
  }

  return (
    <Paper ref={chartRef} p={{ base: 'sm', md: 'lg' }} withBorder radius={12} bg="var(--rdh-card-bg)">
      <ChartHeader
        title="Voltage"
        subtitle={isMobile ? '(V)' : undefined}
        chartRef={chartRef}
      />

      <ResponsiveContainer width="100%" height={chartHeight}>
        <ComposedChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} vertical={false} />

          <XAxis
            dataKey="timestamp"
            type="number"
            domain={[startDate.getTime(), endDate.getTime()]}
            ticks={timeAxis.ticks}
            tickFormatter={timeAxis.formatTick}
            tick={CHART_AXIS_TICK}
            axisLine={false}
            tickLine={false}
            height={isMobile ? 35 : 50}
          />

          <YAxis
            domain={yAxisConfig.domain}
            ticks={yAxisConfig.ticks}
            tickFormatter={yAxisConfig.tickFormatter}
            width={isMobile ? 35 : 60}
            tick={CHART_AXIS_TICK}
            axisLine={false}
            tickLine={false}
          />

          <Tooltip
            cursor={{
              stroke: CHART_CROSSHAIR_COLOR,
              strokeWidth: 1,
              strokeDasharray: '4 4',
            }}
            content={
              <ChartTooltipContent
                valueFormatter={(v) => `${v.toFixed(1)}V`}
              />
            }
          />

          <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={CHART_LEGEND_STYLE} />

          {voltageChannels.map((ch, i) => (
            <Line
              key={ch.did}
              type="linear"
              dataKey={ch.did}
              name={voltageChannels.length === 1
                ? 'Voltage'
                : `Voltage (EB${ch.module_id})`
              }
              stroke={ANALYSIS_SERIES_COLORS[i % ANALYSIS_SERIES_COLORS.length]}
              strokeWidth={1.5}
              dot={false}
              activeDot={{ r: 6 }}
              connectNulls={false}
              isAnimationActive={false}
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </Paper>
  );
}
